/**
 * Ruoli e rotte lato frontend (come gestionale-gls/src/utils/roleHelper.js).
 *
 * Il server è l'autorità (/getUserConfig, /auth/validate); qui si tiene una
 * copia in memoria per decidere subito cosa mostrare. Una pagina nuova va
 * autorizzata in isRouteAllowed, altrimenti ProtectedRoute la nega in
 * silenzio (lezione imparata su gestionale-gls).
 */
import api, { forceTokenRefresh } from "../core/api";
import { TOKEN_KEY, ID_AZIENDA_KEY } from "../core/storageKeys";

let serverConfig = null;
let lastFetchTime = 0;
const CACHE_DURATION = 5 * 60 * 1000;
const listeners = new Set();

export function decodeToken(token = localStorage.getItem(TOKEN_KEY)) {
  if (!token) return null;
  try {
    const payload = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    // Il nome può avere accenti: atob restituisce byte, vanno decodificati come UTF-8.
    const bytes = Uint8Array.from(atob(payload), (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    return null;
  }
}

export function onConfigChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export async function fetchUserConfig(forceRefresh = false) {
  if (!forceRefresh && serverConfig && Date.now() - lastFetchTime < CACHE_DURATION) return serverConfig;
  try {
    const res = await api.get("/getUserConfig");
    serverConfig = res.data;
    lastFetchTime = Date.now();
    if (serverConfig?.user?.idAzienda) localStorage.setItem(ID_AZIENDA_KEY, String(serverConfig.user.idAzienda));
    listeners.forEach((fn) => fn(serverConfig));
    return serverConfig;
  } catch (error) {
    const decoded = decodeToken();
    return { success: false, user: decoded, allowedRoutes: [], allowedMenuItems: [], fallback: true, status: error?.response?.status };
  }
}

export async function refreshUserToken() {
  const token = await forceTokenRefresh();
  serverConfig = null;
  await fetchUserConfig(true);
  return { token };
}

export const getServerConfig = () => serverConfig;

export function clearConfig() {
  serverConfig = null;
  lastFetchTime = 0;
}

export function getUserData() {
  return serverConfig?.user || decodeToken();
}

export function isAdmin() {
  const u = getUserData();
  return Boolean(u && (u.isAdmin === true || u.isAdmin === 1));
}

export function hasCap(cap) {
  const u = serverConfig?.user;
  if (!u) return false;
  return Boolean(u.isAdmin || (u.caps && u.caps[cap]));
}

const ALWAYS_ALLOWED = ["/gestisciprofilo", "/gestisciazienda"];
const ADMIN_ROUTES = ["/gestisciutenti", "/gestisciruoli", "/gestioneabbonamento", "/configurazione"];

/** Sezioni dell'app per rotta (la chiave è quella di config/permissions.js del backend). */
const PAGINE = {
  "/clienti": "clienti",
  "/dipendenti": "dipendenti",
  "/presenze-hr": "presenze",
  "/scadenze": "scadenze",
  "/importa": "importa",
  "/buste-paga": "paghe",
};

/**
 * @param {string} routePath  percorso corrente (anche con parametri)
 * @param {object} authData   esito di /auth/validate ({ isAdmin, pages })
 */
export function isRouteAllowed(routePath, authData = null) {
  const first = `/${String(routePath || "/").split("/")[1] || ""}`.toLowerCase();
  if (first === "/") return true;
  if (ALWAYS_ALLOWED.includes(first)) return true;
  const admin = authData ? authData.isAdmin === true : isAdmin();
  if (ADMIN_ROUTES.includes(first)) return admin;
  if (admin) return true;
  const pages = authData?.pages || serverConfig?.user?.pages;
  if (!pages) return true; // configurazione non ancora caricata
  if (PAGINE[first]) return pages.includes(PAGINE[first]);
  return true;
}

export function getMenuItems() {
  return serverConfig?.allowedMenuItems || [];
}

export function firstAllowedPath() {
  // La Panoramica c'è per tutti: è sempre la prima pagina.
  return "/";
}
